import {createPortal} from 'react-dom';
import {IRole, TRecipientType} from '@verdocs/js-sdk';
import React, {FC, useEffect, useRef, useState} from 'react';
import {
  PencilSquareIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

interface RoleToolbarProps {
  role: IRole;
  anchorEl: HTMLElement | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onTypeChange: (type: TRecipientType) => void;
  onNameChange: (name: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onSettings: () => void;
  onClose: () => void;
}

const TYPE_OPTIONS: {value: TRecipientType; label: string; Icon: FC<{className?: string}>}[] = [
  {value: 'signer', label: 'Signer', Icon: PencilSquareIcon},
  {value: 'cc', label: 'CC', Icon: EnvelopeIcon},
  {value: 'approver', label: 'Approver', Icon: CheckBadgeIcon},
];

export const RoleToolbar: FC<RoleToolbarProps> = ({
  role,
  anchorEl,
  canMoveUp,
  canMoveDown,
  onTypeChange,
  onNameChange,
  onMoveUp,
  onMoveDown,
  onDelete,
  onSettings,
  onClose,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({top: 0, left: 0, width: 0});
  const [isMobile, setIsMobile] = useState(false);
  const [localName, setLocalName] = useState(role.name);

  useEffect(() => {
    setLocalName(role.name);
  }, [role.name]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!anchorEl || isMobile) return;

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: Math.max(rect.width, 360),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorEl, isMobile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node) && anchorEl && !anchorEl.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorEl, onClose]);

  const handleNameBlur = () => {
    const trimmed = localName.trim();
    if (trimmed && trimmed !== role.name) {
      onNameChange(trimmed);
    } else {
      setLocalName(role.name);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setLocalName(role.name);
      (e.target as HTMLInputElement).blur();
    }
  };

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  const toolbar = (
    <div
      ref={toolbarRef}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      className={
        isMobile
          ? 'fixed bottom-0 left-0 right-0 z-[400] bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-4 py-3'
          : 'fixed z-[400] bg-white rounded-xl border border-gray-200 shadow-xl px-4 py-3'
      }
      style={isMobile ? undefined : {top: position.top, left: position.left, minWidth: position.width}}>
      <div className="flex flex-col gap-2.5">
        {/* Type selector — segmented control */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {TYPE_OPTIONS.map(({value, label, Icon}) => (
            <button
              key={value}
              onClick={() => onTypeChange(value)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                role.type === value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Name + action buttons */}
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-800 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="Role name"
          />

          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="p-1.5 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent border-r border-gray-200"
              title="Move to previous step">
              <ChevronUpIcon className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="p-1.5 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent"
              title="Move to next step">
              <ChevronDownIcon className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200 text-gray-500 hover:text-red-600 transition-colors"
            title="Delete role">
            <TrashIcon className="w-4 h-4" />
          </button>

          <button
            onClick={onSettings}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
            title="Role settings">
            <Cog6ToothIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(toolbar, document.body);
};
